import { apiClient } from './apiClient';
import { buildReportQueryString, fetchAllPages, REPORT_EXPORT_PAGE_SIZE } from './reportPaging';

export interface CostingTransactionLogDto {
  id: number;
  timesheetId: number;
  timesheetLineId: number;
  employeeId: number;
  employeeName?: string | null;
  resourceCode?: string | null;
  periodYear?: string | null;
  periodNumber?: number | null;
  transactionDate?: string | null;
  reference?: string | null;
  projectCode?: string | null;
  projectName?: string | null;
  costCentreCode?: string | null;
  costCentreName?: string | null;
  quantity?: number | null;
  price?: number | null;
  upliftValue?: number | null;
  success: boolean;
  externalTransactionId?: string | null;
  errorMessage?: string | null;
}

export interface CostingIntegrationStatus {
  isConfigured: boolean;
  productTypeCode?: string | null;
  productTypeName?: string | null;
  instanceName?: string | null;
}

export interface PagedCostingTransactionLogResponse {
  items: CostingTransactionLogDto[];
  totalCount: number;
  postedCount: number;
  failedCount: number;
  pageNumber: number;
  pageSize: number;
  integration: CostingIntegrationStatus;
}

export type PostingLogStatusFilter = 'posted' | 'failed';

export interface PostingLogQueryParams {
  employeeIds?: number[];
  projectCodes?: string[];
  costCentreCodes?: string[];
  departments?: string[];
  timesheetId?: number;
  status?: PostingLogStatusFilter;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface ResendCostingTransactionResult {
  status: string;
  transactionId?: string | null;
  errors: string[];
  warnings: string[];
  success: boolean;
}

export const NO_ERP_INTEGRATION_MESSAGE = 'No ERP integration configured';

export function buildPostingLogQueryString(params: PostingLogQueryParams): string {
  return buildReportQueryString({
    employeeIds: params.employeeIds,
    projectCodes: params.projectCodes,
    costCentreCodes: params.costCentreCodes,
    departments: params.departments,
    timesheetId: params.timesheetId,
    status: params.status,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    search: params.search?.trim(),
    pageNumber: params.pageNumber,
    pageSize: params.pageSize,
    sortBy: params.sortBy,
    sortDir: params.sortDir,
  });
}

export const postingLogApi = {
  async getPostingLog(params: PostingLogQueryParams = {}): Promise<PagedCostingTransactionLogResponse> {
    return apiClient.get<PagedCostingTransactionLogResponse>(`/costing-transactions${buildPostingLogQueryString(params)}`);
  },

  /** Every row matching the filters, fetched page by page at the server's maximum page size. */
  async getAllPostingLogRows(
    params: Omit<PostingLogQueryParams, 'pageNumber' | 'pageSize'> = {},
  ): Promise<CostingTransactionLogDto[]> {
    return fetchAllPages(
      (pageNumber, pageSize) => postingLogApi.getPostingLog({ ...params, pageNumber, pageSize }),
      REPORT_EXPORT_PAGE_SIZE,
    );
  },

  async resend(logId: number): Promise<ResendCostingTransactionResult> {
    return apiClient.post<ResendCostingTransactionResult, Record<string, never>>(`/costing-transactions/${logId}/resend`, {});
  },
};
